import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Modal from '@/assets/common/Modal/Modal.vue';
import FertilizerPairSplitModal from '@/assets/common/Modal/FertilizerPairSplitModal/FertilizerPairSplitModal.vue';
import { WarehouseFertilizerWithWPU } from '@/types/Component';

type FertilizerUsage = {
    id: string | null;
    fertilizerId: string | null;
    fertilizerName: string | null;
    chemRetio: number | null;
    warehouseId : string | null;
    warehouseName : string | null;
    workRecordId: string | null;
    workDate: Date | null;
    usageQty: number | null;
    usageUnit: string | null;
    prevUsageUnit?: string | null;
    unitOfMeasure: number;
    usedDates: Date[];
    editable: boolean;
    amount: number | null;
    updated: string | null;
    transactions?: Transaction[];
    shares?: PairShare[] | null;
    processType?: string | null;
}

type Transaction = {
    id: string | null;
    updated: string | null;
};

type SplitPair = {
    plantingId: string | null;
    tempPlantingKey: string | null;
    agriFieldId: string;
    label: string;
    area: number | null;
};

type PairShare = {
    plantingId: string | null;
    tempPlantingKey?: string | null;
    agriFieldId: string;
    usageQty: number | null;
    amount: number | null;
};

@Component({
    components: {
        Modal,
        FertilizerPairSplitModal,
    }
})
export default class FertilizerUsageModal extends Vue {

    @Prop() 
    showFertilizerUsageModal!: boolean

    @Prop()
    initialValue!: FertilizerUsage;

    @Prop()
    pdTenantId!: string;

    @Prop()
    tenantId?: string;

    @Prop({ default: false })
    isBulkContext!: boolean;

    @Prop({ default: false })
    isReadOnly!: boolean;

    @Prop({ default: () => [] })
    bulkPairs!: SplitPair[];

    form: FertilizerUsage = this.createEmptyForm();

    isUnknownDate = false;
    altered = false;
    showSplitModal = false;
    private fertilizerUnits = [] as any[];
    private warehouseFertilizers = [] as WarehouseFertilizerWithWPU[];
    private lastSyncedUsageQty: number | null = null;
    private lastSyncedAmount: number | null = null;
    private lastSyncedUnitOfMeasure: number | null = null;
    splitNoticeFields: string[] = [];

    formatNumberWithCommas = (v: number) => (v % 1 ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v.toLocaleString());

    createEmptyForm(): FertilizerUsage {
        return {
            id: null,
            fertilizerId: null,
            fertilizerName: null,
            chemRetio: null,
            warehouseId: null,
            warehouseName: null,
            workRecordId: null,
            workDate: null,
            usageQty: null,
            usageUnit: "kg",
            prevUsageUnit: "kg",
            unitOfMeasure: 0,
            usedDates: [],
            editable: false,
            amount: null,
            updated: null,
            transactions: [],
            shares: null,
            processType: null,
        };
    }

    @Watch('showFertilizerUsageModal')
    onModalOpen(val: boolean) {
        this.form = this.createEmptyForm();
        if (val) {
            this.form = this.initialValue ? { ...this.initialValue } : this.createEmptyForm();
            this.form.prevUsageUnit = this.form.usageUnit;
            this.isUnknownDate = !this.form.workDate;
            this.altered = false;
            this.splitNoticeFields = [];
            this.lastSyncedUsageQty = this.form.usageQty;
            this.lastSyncedAmount = this.form.amount;
            this.lastSyncedUnitOfMeasure = this.form.unitOfMeasure;
            this.$nextTick(() => (this.$refs.usageQtyInput as HTMLInputElement)?.focus());
        }
        this.getFertilizerUnits();
        this.getWarehouseFertilizers();
    }

    getWarehouseFertilizers() {
        if (!this.pdTenantId) return;
        this.$apiClient.doRequest('get', `/admin/warehouses/warehouse_fertilizers/${this.pdTenantId}?sp_tenant_id=${this.tenantId || ''}`).then((res) => {
            this.warehouseFertilizers = res.warehouseFertilizers || [];
        })
    }

    handleTab(e: KeyboardEvent) {
        const shift = e.shiftKey;
        const usageQtyInput = this.$refs.usageQtyInput as HTMLInputElement;
        const usageAmountInput = this.$refs.usageAmountInput as HTMLInputElement;
        const confirmBtn = this.$refs.confirmBtn as HTMLButtonElement;
        const closeBtn = this.$refs.closeBtn as HTMLButtonElement;
        const deleteBtn = this.$refs.deleteBtn as HTMLButtonElement;
        const focusOrder: HTMLElement[] = [
            usageQtyInput,
            usageAmountInput,
            confirmBtn,
            closeBtn,
            deleteBtn,
        ].filter(Boolean) as HTMLElement[];
        const currentIndex = focusOrder.indexOf(document.activeElement as HTMLElement);
        if (currentIndex === -1) return;
            if (!shift) {
                if (currentIndex < focusOrder.length - 1) {
                    e.preventDefault();
                    focusOrder[currentIndex + 1].focus();
                }
            } else {
                if (currentIndex > 0) {
                    e.preventDefault();
                    focusOrder[currentIndex - 1].focus();
                }
            }
    }

    @Watch('form.workDate')
    onWorkDateChanged(val: Date | null) {
        if (!this.form.warehouseId) {
            if (val) {
                this.isUnknownDate = false;
            } else {
                this.isUnknownDate = true;
            }
        }
    }

    async save(flag: boolean): Promise<void> {
        if (flag) {
            const errors: string[] = [];
            const regex = /^\d{1,7}(\.\d{1,2})?$/;
            const re = /^\d{1,10}$/;
            const { id, workRecordId, workDate, usageQty, usageUnit, unitOfMeasure, warehouseId, amount, updated, transactions, processType } = this.form;

            const usageQtyStr = usageQty != null ? usageQty.toString().trim() : '';
            const usageQtyNum = Number(usageQtyStr);
            const isUsageQtyFilled = usageQtyStr !== '';
            const isUsageUnitFilled = usageUnit != null && usageUnit !== '';

            if (warehouseId && !workDate) {
                errors.push('使用日を入力してください。');
            }

            if (workDate && this.form.usedDates.some(date => new Date(date).toDateString() === new Date(workDate).toDateString())) {
                errors.push('既に同じ使用日で肥料の登録があります。別の日付を選択してください。');
            }

            if (!isUsageQtyFilled) {
                errors.push('使用量を入力してください。');
            }

            if (isUsageQtyFilled && (!regex.test(usageQtyStr) || usageQtyNum <= 0 || isNaN(usageQtyNum))) {
                errors.push('使用量は0より大きい整数部7桁、小数部2桁までの数値を入力してください。');
            }

            if (!isUsageUnitFilled) {
                errors.push('使用量の単位を入力してください。');
            }

            const amountStr = amount != null ? amount.toString().trim() : '';
            const isAmountFilled = amountStr !== '';
            if (isAmountFilled && !re.test(amountStr)) {
                errors.push('金額は10桁以内で入力してください。');
            }

            if (errors.length === 0 && this.bulkPairs.length >= 2) {
                const shares = this.form.shares;
                if (!shares || shares.length === 0 || !this.pairSetMatchesShares(shares)) {
                    errors.push('入力した値と明細の値が一致しません。明細を開いて再設定してください。');
                } else {
                    const isRate = this.form.unitOfMeasure === 1;
                    if (!this.isPoolFieldConsistent(shares, usageQty, 'usageQty', isRate, 2)) {
                        errors.push(isRate
                            ? '使用量が明細の値と一致しません。使用量を再入力してください。'
                            : '使用量が明細の値と一致しません。明細を開いて再設定してください。');
                    }
                    if (!this.isPoolFieldConsistent(shares, amount, 'amount', false, 0)) {
                        errors.push('金額が明細の値と一致しません。明細を開いて再設定してください。');
                    }
                }
            }

            if (errors.length > 0) {
                window.alert(errors.join('\n'));
                return;
            }

            if (this.pdTenantId && workRecordId && workDate && this.initialValue.workDate && workDate != this.initialValue.workDate) {
                const res = await this.$apiClient.doRequest('get', `/work_records/check_overlapping_workers/${this.pdTenantId}/${workRecordId}/${workDate}`);
                if (res) {
                    window.alert(`指定した使用日(${this.formatDate(workDate)})に、作業者が登録された作業記録が存在する為、変更できません。`);
                    return;
                }
            }

            this.$emit('update:showFertilizerUsageModal', false);
            const shares = this.form.shares;
            this.$emit('save', { id, workRecordId, workDate, usageQty, usageUnit, unitOfMeasure, amount, updated, transactions, shares, processType });
        } else {
            if (!window.confirm('削除しますか？')) return;
            this.$emit('update:showFertilizerUsageModal', false);
            this.$emit('save', null);
        }
    }

    alteredForm() {
        this.altered = true;
    }

    onToggleUnknownDate() {
        if (this.isUnknownDate) {
            this.form.workDate = null;
        }
        this.alteredForm();
    }

    get chisso(): string {
        const { usageQty: q, chemRetio: r, usageUnit: u, unitOfMeasure: m } = this.form;
        if (q == null || r == null || !u) return '---';
        const fertUnit = this.fertilizerUnits?.find(unit => unit.name === u);
        const qtyInKg = q * fertUnit.weight_per_unit;
        return `${this.formatNumberWithCommas((qtyInKg * r) / 100)} kg${m === 1 ? '/10a' : ''}`;
    }

    close() {
        if (!this.altered || window.confirm("変更内容を破棄します。よろしいですか？")) {
            this.form = this.createEmptyForm();
            this.altered = false;
            this.isUnknownDate = false;
            this.$emit('update:showFertilizerUsageModal', false);
            this.warehouseFertilizers = [];
        }
    }

    formatDate(date: Date): string {
        const dt = new Date(date);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}`;
    }

    validateNum(field:any) {
        if (field === 'usageQty') {
            if (this.form.usageQty === '' as any || this.form.usageQty === null || this.form.usageQty === undefined) {
                return;
            }
            const num = Number(this.form.usageQty);
            this.form.usageQty = isNaN(num) ? null : num;
        }
        if (field === 'amount') {
            if (this.form.amount === '' as any || this.form.amount === null || this.form.amount === undefined) {
                return;
            }
            const num = Number(this.form.amount);
            this.form.amount = isNaN(num) ? null : num;
        }
    }

    handleUnitChange() {
        const item = this.form;
        if (!item.usageUnit) return;
        if (!item.prevUsageUnit) {
            item.prevUsageUnit = item.usageUnit;
            return;
        }
        if (item.prevUsageUnit === item.usageUnit) return;
        const oldUnit = item.prevUsageUnit;
        const newUnit = item.usageUnit;
        const newValue = this.convertUnit(Number(item.usageQty), oldUnit, newUnit);
        item.usageQty = newValue;
        item.prevUsageUnit = newUnit;
        this.alteredForm();
    }

    convertUnit(value: number, fromUnit: string, toUnit: string): number {
        const baseValue = this.convertToKgL(value, fromUnit);
        return this.convertFromKgL(baseValue, toUnit);
    }

    getUnit(unit: string) {
       return this.fertilizerUnits?.find(u => u.name === unit);
    }

    convertToKgL(value: number, unit: string): number {
       const fertUnit = this.getUnit(unit);

        if (!fertUnit || !fertUnit.weight_per_unit) {
            return value;
        }

        return value * fertUnit.weight_per_unit;
    }
      
    convertFromKgL(value: number, unit: string): number {
        const fertUnit = this.getUnit(unit);

        if (!fertUnit || !fertUnit.weight_per_unit) {
            return value;
        }

        return value / fertUnit.weight_per_unit;
    }

    getFertilizerUnits() {
        this.$apiClient.doRequest('get','/unit_conversion/fertilizer').then((res) => {
        this.fertilizerUnits = res.weight_units
        })
    }

    filteredFertilizerUnits(fertilizerId: string | null, warehouseId: string | null): any[] {
        if (!fertilizerId) return this.fertilizerUnits;
        const wc = this.warehouseFertilizers.find(w => (w.fertilizerId === fertilizerId || w.resolvedFertilizerId === fertilizerId) && (w.warehouseId === warehouseId || w.warehouseName === '未管理'));
        if (!wc) return this.fertilizerUnits;
        const isWeightNull = wc.weightPerUnit == null;
        return this.fertilizerUnits.filter(unit => isWeightNull ? unit.weight_per_unit == null : unit.weight_per_unit != null);
    }

    syncSharesFromForm() {
        const { usageQty, amount, unitOfMeasure } = this.form;
        if (usageQty === this.lastSyncedUsageQty && amount === this.lastSyncedAmount
            && unitOfMeasure === this.lastSyncedUnitOfMeasure) return;
        if (this.bulkPairs.length >= 2) {
            const result = this.recomputeStaleFields(usageQty, amount);
            if (result.changedFields.length > 0) {
                this.form.shares = result.shares;
                this.splitNoticeFields = this.mergeSplitNoticeFields(result.changedFields);
            }
        }
        this.lastSyncedUsageQty = usageQty;
        this.lastSyncedAmount = amount;
        this.lastSyncedUnitOfMeasure = unitOfMeasure;
    }

    onUnitOfMeasureChange() {
        if (this.bulkPairs.length >= 2) {
            const { usageQty, amount, unitOfMeasure } = this.form;
            const result = this.forceResplitAllFields(usageQty, amount);
            this.form.shares = result.shares;
            this.lastSyncedUsageQty = usageQty;
            this.lastSyncedAmount = amount;
            this.lastSyncedUnitOfMeasure = unitOfMeasure;
            if (result.changedFields.length > 0) {
                this.splitNoticeFields = this.mergeSplitNoticeFields(result.changedFields);
            }
        }
        if (this.isBulkContext) {
            window.alert(this.form.unitOfMeasure === 1
                ? '明細は自動按分されます。'
                : '明細は自動で按分されます。個別に設定が必要な場合は、明細の再設定を行ってください。');
        }
    }

    openSplitModal() {
        this.showSplitModal = true;
    }

    onSplitSave(payload: { shares: PairShare[]; totalUsageQty: number | null; totalAmount: number | null }) {
        this.form.usageQty = payload.totalUsageQty;
        this.form.amount = payload.totalAmount;
        this.form.shares = payload.shares;
        this.lastSyncedUsageQty = payload.totalUsageQty;
        this.lastSyncedAmount = payload.totalAmount;
        this.lastSyncedUnitOfMeasure = this.form.unitOfMeasure;
        this.splitNoticeFields = [];
        this.alteredForm();
    }

    private pairSetMatchesShares(shares: PairShare[]): boolean {
        const bulkKeys = this.bulkPairs.map(p => this.sharePairKey(p.plantingId, p.tempPlantingKey, p.agriFieldId));
        return shares.length === bulkKeys.length
            && bulkKeys.every(k => shares.some(s => this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId) === k));
    }

    private isPoolFieldConsistent(shares: PairShare[], total: number | null, field: 'usageQty' | 'amount', isRate: boolean, decimals: number): boolean {
        if (total == null) return true;
        if (shares.some(s => s[field] == null)) return false;
        const factor = Math.pow(10, decimals);
        return isRate
            ? shares.every(s => Math.round((s[field] ?? 0) * factor) === Math.round(total * factor))
            : Math.round(shares.reduce((sum, s) => sum + (s[field] ?? 0), 0) * factor) === Math.round(total * factor);
    }

    private recomputeStaleFields(usageQty: number | null, amount: number | null): { shares: PairShare[]; changedFields: string[] } {
        if (this.bulkPairs.length === 0) return { shares: [], changedFields: [] };
        let shares = this.ensurePairShares();
        const isRate = this.form.unitOfMeasure === 1;
        const changedFields: string[] = [];

        if (usageQty != null && !this.isPoolFieldConsistent(shares, usageQty, 'usageQty', isRate, 2)) {
            const usageShares = this.splitByArea(usageQty, 2);
            shares = shares.map((s, i) => ({ ...s, usageQty: usageShares[i] }));
            changedFields.push('使用量');
        }
        if (amount != null && !this.isPoolFieldConsistent(shares, amount, 'amount', false, 0)) {
            const amountShares = this.splitAmountByArea(amount, 0);
            shares = shares.map((s, i) => ({ ...s, amount: amountShares[i] }));
            changedFields.push('金額');
        }

        return { shares, changedFields };
    }

    /**
     * Unconditionally re-splits every applicable field for the current mode, regardless of
     * whether shares already looked consistent. Used on unitOfMeasure change, since switching
     * mode changes how usageQty should be distributed even when the pool check happens to still pass.
     */
    private forceResplitAllFields(usageQty: number | null, amount: number | null): { shares: PairShare[]; changedFields: string[] } {
        if (this.bulkPairs.length === 0) return { shares: [], changedFields: [] };
        let shares = this.ensurePairShares();
        const changedFields: string[] = [];

        const usageShares = this.splitByArea(usageQty, 2);
        shares = shares.map((s, i) => ({ ...s, usageQty: usageShares[i] }));
        changedFields.push('使用量');

        const amountShares = this.splitAmountByArea(amount, 0);
        shares = shares.map((s, i) => ({ ...s, amount: amountShares[i] }));
        changedFields.push('金額');

        return { shares, changedFields };
    }

    private splitByArea(total: number | null, decimals: number): (number | null)[] {
        if (total == null) return this.bulkPairs.map(() => null);
        if (this.form.unitOfMeasure === 1) {
            const factor = Math.pow(10, decimals);
            const rate = Math.round(total * factor) / factor;
            return this.bulkPairs.map(() => rate);
        }
        return this.splitAmountByArea(total, decimals);
    }

    private splitAmountByArea(total: number | null, decimals: number): (number | null)[] {
        if (total == null) return this.bulkPairs.map(() => null);
        const count = this.bulkPairs.length;
        const factor = Math.pow(10, decimals);
        const areas = this.bulkPairs.map(p => p.area);
        const useArea = areas.every(a => a != null && a > 0);
        let totalArea = 0;
        if (useArea) {
            for (const a of areas) totalArea += a as number;
        }
        const shares = this.bulkPairs.map((_, i) => {
            const raw = useArea ? (total * (areas[i] as number)) / totalArea : total / count;
            return Math.floor(raw * factor) / factor;
        });
        const sum = shares.reduce((s, v) => s + v, 0);
        const remainder = Math.round((total - sum) * factor) / factor;
        shares[0] = Math.round((shares[0] + remainder) * factor) / factor;
        return shares;
    }

    private sharePairKey(plantingId: string | null | undefined, tempPlantingKey: string | null | undefined, agriFieldId: string | null | undefined): string {
        return `${plantingId ?? tempPlantingKey ?? ''}_${agriFieldId ?? ''}`;
    }

    get splitNoticeMessage(): string | null {
        if (this.splitNoticeFields.length === 0) return null;
        return `明細（${this.splitNoticeFields.join('、')}）が計算されました。明細を確認してください。`;
    }

    private mergeSplitNoticeFields(newFields: string[]): string[] {
        const order = ['使用量', '金額'];
        const merged = new Set([...this.splitNoticeFields, ...newFields]);
        return order.filter(f => merged.has(f));
    }

    private ensurePairShares(): PairShare[] {
        const existing = this.form.shares ?? [];
        const existingByKey = new Map(existing.map(s => [this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId), s]));
        return this.bulkPairs.map(p => {
            const prior = existingByKey.get(this.sharePairKey(p.plantingId, p.tempPlantingKey, p.agriFieldId));
            return {
                plantingId: p.plantingId,
                tempPlantingKey: p.tempPlantingKey,
                agriFieldId: p.agriFieldId,
                usageQty: prior?.usageQty ?? null,
                amount: prior?.amount ?? null,
            };
        });
    }
}
